/**
 * Embed configuration — the single source of truth for how this app instance
 * is hosted.
 *
 * Two hosting modes:
 *   "standalone"  order.app2food.com/olo/{storeId}      (the original product)
 *   "embed"       website1.com/online-ordering#/menu    (mounted on a client domain)
 *
 * IMPORTANT: this module resolves its config EAGERLY, at import time, before any
 * other module body runs. Several modules read the store id while initialising
 * (notably store/storeSlice.ts, which builds Redux initial state at import), so
 * config must already be available by then. The loader script therefore sets
 * window.__A2F_CONFIG__ *before* loading the app bundle. If it isn't set we fall
 * back to reading the DOM, then to standalone URL parsing — which reproduces the
 * original behaviour byte for byte.
 */

export type RouterMode = "path" | "hash";
export type HostingMode = "standalone" | "embed";

export interface EmbedConfigInput {
  storeId?: string;
  /** "hash" (default in embed mode) needs no server config on the client domain. */
  routerMode?: RouterMode;
  /** Path the embed is served under, e.g. "/online-ordering". Path mode only. */
  basePath?: string;
  apiBase?: string;
  /** Absolute CDN prefix for bundled assets. Baked in at build time; override rarely. */
  assetBase?: string;
  /** localStorage/sessionStorage key prefix. Defaults to "a2f:{storeId}:" in embed mode. */
  storagePrefix?: string;
  /** Let the embed set document.title / favicon. Off by default — it's the client's page. */
  manageTitle?: boolean;
  /** Injected as CSS custom properties, so a client can brand without a rebuild. */
  theme?: Record<string, string>;
  /** Load Google Tag Manager. Off by default on client domains. */
  gtm?: string | false;
}

export interface EmbedConfig {
  storeId: string;
  mode: HostingMode;
  routerMode: RouterMode;
  /** Passed straight to the router. "" for hash mode. */
  basename: string;
  apiBase: string;
  assetBase: string;
  storagePrefix: string;
  manageTitle: boolean;
  theme: Record<string, string>;
  gtm: string | false;
}

declare global {
  interface Window {
    __A2F_CONFIG__?: EmbedConfigInput;
  }
}

const ENV_API_BASE = import.meta.env.VITE_APP_API_URL as string;

/** Reads the original URL scheme: /olo/{storeId}/... */
function storeIdFromPathname(): string {
  if (typeof window === "undefined") return "";
  return window.location.pathname.split("/")[2] ?? "";
}

/**
 * Last-resort config discovery, so the bundle also works when dropped on a page
 * without the loader having set the global.
 */
function configFromDom(): EmbedConfigInput | null {
  if (typeof document === "undefined") return null;

  const el = document.querySelector<HTMLElement>(
    "script[data-store-id], app2food-ordering[store-id], [data-a2f-store-id]",
  );
  if (!el) return null;

  const storeId =
    el.getAttribute("data-store-id") ??
    el.getAttribute("store-id") ??
    el.getAttribute("data-a2f-store-id") ??
    undefined;
  if (!storeId) return null;

  const routerMode = el.getAttribute("data-router") as RouterMode | null;

  return {
    storeId,
    routerMode: routerMode === "path" || routerMode === "hash" ? routerMode : undefined,
    basePath: el.getAttribute("data-base-path") ?? undefined,
    apiBase: el.getAttribute("data-api-base") ?? undefined,
    manageTitle: el.getAttribute("data-manage-title") === "true" || undefined,
  };
}

function normalisePath(path: string): string {
  if (!path) return "";
  const withLeading = path.startsWith("/") ? path : `/${path}`;
  return withLeading.endsWith("/") && withLeading.length > 1
    ? withLeading.slice(0, -1)
    : withLeading;
}

function resolve(): EmbedConfig {
  const input: EmbedConfigInput | null =
    (typeof window !== "undefined" && window.__A2F_CONFIG__) || configFromDom();

  // ---- standalone: the original /olo/{storeId} product, unchanged ----
  if (!input || !input.storeId) {
    const storeId = storeIdFromPathname();
    return {
      storeId,
      mode: "standalone",
      routerMode: "path",
      basename: `/olo/${storeId}`,
      apiBase: ENV_API_BASE,
      assetBase: import.meta.env.BASE_URL,
      storagePrefix: "", // must stay empty: live customers have unprefixed keys
      manageTitle: true,
      theme: {},
      gtm: false, // standalone GTM stays in index.html, as today
    };
  }

  // ---- embed: mounted on a client domain ----
  const storeId = String(input.storeId);
  const routerMode: RouterMode = input.routerMode ?? "hash";
  const basePath = normalisePath(input.basePath ?? "");

  return {
    storeId,
    mode: "embed",
    routerMode,
    // HashRouter owns everything after "#", so the page path is not its concern.
    basename: routerMode === "hash" ? "" : basePath,
    apiBase: input.apiBase ?? ENV_API_BASE,
    assetBase: input.assetBase ?? import.meta.env.BASE_URL,
    storagePrefix: input.storagePrefix ?? `a2f:${storeId}:`,
    manageTitle: input.manageTitle ?? false,
    theme: input.theme ?? {},
    gtm: input.gtm ?? false,
  };
}

let config: EmbedConfig = resolve();

export function getEmbedConfig(): EmbedConfig {
  return config;
}

export function isEmbedded(): boolean {
  return config.mode === "embed";
}

/**
 * Applied by mount() for values that can safely change after import (theme,
 * title handling, api base). storeId and routerMode are deliberately NOT
 * overridable here: Redux initial state and the storage prefix are already
 * derived from them by the time mount() runs. See mountOrdering() for the guard.
 */
export function applyLateConfig(
  patch: Pick<EmbedConfigInput, "apiBase" | "manageTitle" | "theme" | "gtm">,
): EmbedConfig {
  config = {
    ...config,
    apiBase: patch.apiBase ?? config.apiBase,
    manageTitle: patch.manageTitle ?? config.manageTitle,
    theme: patch.theme ?? config.theme,
    gtm: patch.gtm ?? config.gtm,
  };
  return config;
}
